/**
 * Audio device hot-swap monitor.
 *
 * Listens for system device changes (kulaklık takıldı/çıkarıldı, USB cihaz
 * disconnect/reconnect, Bluetooth pair/unpair) and notifies subscribers when
 * the currently-selected mic or speaker disappears. JanusSip uses this to
 * swap the mic track mid-call without dropping the connection.
 *
 * Browser quirks:
 *  - Chrome fires `devicechange` on the singleton mediaDevices object reliably.
 *  - Firefox is less reliable for output-only changes — we fall back to window
 *    focus re-enumeration as a safety net.
 *  - Device IDs are stable across reconnects of the SAME physical device but
 *    rotate per origin; label-based matching is needed for human display.
 */

import { useAudio } from '@/store/audio';

export type DeviceKind = 'audioinput' | 'audiooutput';
export interface DeviceSnapshot {
  inputs: MediaDeviceInfo[];
  outputs: MediaDeviceInfo[];
}

type DeviceListener = (event: DeviceChangeEvent) => void;
export interface DeviceChangeEvent {
  snapshot: DeviceSnapshot;
  /** Devices that exist now but didn't before (USB plugged in, headset paired). */
  added: MediaDeviceInfo[];
  /** Devices that disappeared (headset unplugged). */
  removed: MediaDeviceInfo[];
  /** True if the user-selected mic device just disappeared. */
  selectedMicLost: boolean;
  /** True if the user-selected speaker device just disappeared. */
  selectedSpeakerLost: boolean;
}

class DeviceMonitor {
  private last: DeviceSnapshot = { inputs: [], outputs: [] };
  private listeners = new Set<DeviceListener>();
  private started = false;
  private focusHandler?: () => void;

  async start(): Promise<void> {
    if (this.started || typeof navigator === 'undefined' || !navigator.mediaDevices) return;
    this.started = true;
    // Permission-trigger one-shot so labels are populated (some browsers
    // return empty labels until getUserMedia has been granted at least once).
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
    } catch { /* */ }

    this.last = await this.snapshot();
    navigator.mediaDevices.addEventListener('devicechange', this.handleChange);
    // Firefox safety net — re-enumerate on focus.
    this.focusHandler = () => { void this.handleChange(); };
    window.addEventListener('focus', this.focusHandler);
    console.log('[device-monitor] started; inputs=%d outputs=%d', this.last.inputs.length, this.last.outputs.length);
  }

  stop(): void {
    if (!this.started) return;
    navigator.mediaDevices.removeEventListener('devicechange', this.handleChange);
    if (this.focusHandler) window.removeEventListener('focus', this.focusHandler);
    this.listeners.clear();
    this.started = false;
  }

  subscribe(fn: DeviceListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  current(): DeviceSnapshot {
    return this.last;
  }

  private snapshot = async (): Promise<DeviceSnapshot> => {
    const list = await navigator.mediaDevices.enumerateDevices();
    return {
      inputs: list.filter((d) => d.kind === 'audioinput'),
      outputs: list.filter((d) => d.kind === 'audiooutput'),
    };
  };

  private handleChange = async (): Promise<void> => {
    const next = await this.snapshot();
    const prev = this.last;
    const added = diffById(next.inputs.concat(next.outputs), prev.inputs.concat(prev.outputs));
    const removed = diffById(prev.inputs.concat(prev.outputs), next.inputs.concat(next.outputs));
    if (added.length === 0 && removed.length === 0) return; // de-dup focus refresh

    const prefs = useAudio.getState();
    const selectedMicLost = prefs.micDeviceId !== 'default'
      && !next.inputs.some((d) => d.deviceId === prefs.micDeviceId);
    const selectedSpeakerLost = prefs.speakerDeviceId !== 'default'
      && !next.outputs.some((d) => d.deviceId === prefs.speakerDeviceId);

    // Auto-fallback: if selected device disappeared, drop back to system default
    // so the user isn't stuck on a non-existent device.
    if (selectedMicLost) useAudio.getState().update({ micDeviceId: 'default' });
    if (selectedSpeakerLost) useAudio.getState().update({ speakerDeviceId: 'default' });

    this.last = next;
    const event: DeviceChangeEvent = { snapshot: next, added, removed, selectedMicLost, selectedSpeakerLost };
    console.log('[device-monitor] change', {
      added: added.map((d) => `${d.kind}:${d.label || d.deviceId.slice(0, 8)}`),
      removed: removed.map((d) => `${d.kind}:${d.label || d.deviceId.slice(0, 8)}`),
      selectedMicLost, selectedSpeakerLost,
    });
    this.listeners.forEach((fn) => {
      try { fn(event); } catch (e) { console.error('[device-monitor] listener error', e); }
    });
  };
}

function diffById(a: MediaDeviceInfo[], b: MediaDeviceInfo[]): MediaDeviceInfo[] {
  const bIds = new Set(b.map((d) => d.deviceId));
  return a.filter((d) => !bIds.has(d.deviceId));
}

export const deviceMonitor = new DeviceMonitor();
